import { cloneDeep } from 'lodash-es'
import { t } from '@/locales'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import type { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'
import type {
  IAnalysisTestAnswer,
  IAnalysisTestProveBasedColorConfig,
  IAnalysisTestSingleSelectColorConfig,
  IAnalysisTestValueBasedColorConfig,
  TResultColorId,
} from '@team-milva/lab-models/resources'
import type { TAnalysisTestUpdateEntry } from '@/stores/analysisStore'
import type { TCreateAnalysisTestPayload } from '@/stores/analysisTestStore'

export type TAnswerForm = IAnalysisTestAnswer & { _isNew?: boolean }

export type TProveBasedQaTrigger = 'always' | 'proved' | 'not-proved'
export type TSingleSelectQaTrigger = 'always' | 'answers'

export type TValueBasedConditionRow = {
  _id: string
  operator: '<' | '<=' | '>=' | '>'
  value: number | null
  colorID: TResultColorId | null
  textID: string
}

// One staged copy per touched AnalysisTest — combines what today's separate
// Details and Klassificering tabs each hold, since here they're two tabs over
// the same shared staged object (mirrors the Analysis level's Generelt +
// Rapportbeskrivelse sharing one `form`).
export type TAnalysisTestFocusForm = {
  id: string
  type: AnalysisTest['type'] | ''
  name: string
  displayName: string
  isOptional: boolean
  qaTrigger: '' | 'always' | 'proved' | 'not-proved'
  // Single-select QA gate (MIL-551): '' = never. The selection only means something with 'answers'.
  qaMode: '' | 'always' | 'answers'
  qaAnswerIDs: string[]
  hasTooLowToMeasure: boolean
  hasTooHighToMeasure: boolean
  minLimit: number | null
  maxLimit: number | null
  unitID: string
  answers: TAnswerForm[]
  reportDescriptionIDs: string[]
  suffixReportDescriptionIDs: string[]
  answerMode: 'none' | 'optional' | 'required'
  answerOverrides: TAnswerForm[]
  hasSumValue: boolean
  colorConfigEnabled: boolean
  provedColorID: TResultColorId | null
  notProvedColorID: TResultColorId | null
  // Single-select: one colour per answer, keyed by answer ID; absent or null = no colour.
  answerColorIDs: Record<string, TResultColorId | null>
  // `result-texts` item ids for the two prove-based outcomes. '' means "use
  // the default", written back as null and resolved at render time as
  // term.yes / term.no — mirrors how unitID / textID use '' for "none".
  provedTextID: string
  notProvedTextID: string
  conditions: TValueBasedConditionRow[]
}

// Sensible defaults for a brand-new test of the given type — used by the
// Create Analysis Test wizard, which stages the same TAnalysisTestFocusForm
// shape as the edit flow rather than a separate create-only form type.
export function buildEmptyAnalysisTestFocusForm(
  type: AnalysisTest['type'] | ''
): TAnalysisTestFocusForm {
  return {
    id: '',
    type,
    name: '',
    displayName: '',
    isOptional: false,
    qaTrigger: '',
    qaMode: '',
    qaAnswerIDs: [],
    hasTooLowToMeasure: false,
    hasTooHighToMeasure: false,
    minLimit: null,
    maxLimit: null,
    unitID: '',
    answers: [],
    reportDescriptionIDs: [],
    suffixReportDescriptionIDs: [],
    answerMode: 'optional',
    answerOverrides: [],
    hasSumValue: false,
    colorConfigEnabled: false,
    provedColorID: null,
    notProvedColorID: null,
    answerColorIDs: {},
    provedTextID: '',
    notProvedTextID: '',
    conditions: [],
  }
}

function getDisplayName(analysisTest: AnalysisTest, locale: string): string {
  return (
    analysisTest.displayName?.[locale] ??
    Object.values(analysisTest.displayName ?? {}).find((v) => v !== '') ??
    ''
  )
}

export function buildAnalysisTestFocusForm(
  analysisTest: AnalysisTest,
  locale: string
): TAnalysisTestFocusForm {
  const config = analysisTest.colorConfig
  const proveConfig =
    analysisTest.type === 'prove-based' ? (config as IAnalysisTestProveBasedColorConfig | null) : null
  const valueConfig =
    analysisTest.type === 'value-based' ? (config as IAnalysisTestValueBasedColorConfig | null) : null
  const singleSelectConfig =
    analysisTest.type === 'single-select'
      ? (config as IAnalysisTestSingleSelectColorConfig | null)
      : null

  return {
    id: analysisTest.id,
    type: analysisTest.type,
    name: analysisTest.getName(locale),
    displayName: getDisplayName(analysisTest, locale),
    isOptional: analysisTest.isOptional ?? false,
    qaTrigger:
      analysisTest.type === 'prove-based'
        ? ((analysisTest.qaTrigger as TProveBasedQaTrigger | null) ?? '')
        : '',
    qaMode:
      analysisTest.type === 'single-select'
        ? ((analysisTest.qaTrigger as TSingleSelectQaTrigger | null) ?? '')
        : '',
    qaAnswerIDs: analysisTest.type === 'single-select' ? [...(analysisTest.qaAnswerIDs ?? [])] : [],
    hasTooLowToMeasure: analysisTest.type === 'value-based' ? analysisTest.hasTooLowToMeasure : false,
    hasTooHighToMeasure: analysisTest.type === 'value-based' ? analysisTest.hasTooHighToMeasure : false,
    minLimit: analysisTest.type === 'value-based' ? analysisTest.minLimit : null,
    maxLimit: analysisTest.type === 'value-based' ? analysisTest.maxLimit : null,
    unitID:
      analysisTest.type === 'value-based' || analysisTest.type === 'multi-select-number'
        ? analysisTest.unitID
        : '',
    answers:
      analysisTest.type === 'single-select' || analysisTest.type === 'multi-select-text'
        ? (cloneDeep(analysisTest.answers) as TAnswerForm[])
        : [],
    reportDescriptionIDs:
      analysisTest.type === 'multi-select-text' || analysisTest.type === 'multi-select-number'
        ? analysisTest.reportDescriptionIDs
        : [],
    suffixReportDescriptionIDs:
      analysisTest.type === 'multi-select-text' || analysisTest.type === 'multi-select-number'
        ? (analysisTest.suffixReportDescriptionIDs ?? [])
        : [],
    answerMode: analysisTest.type === 'multi-select-text' ? (analysisTest.options.answerMode ?? 'optional') : 'optional',
    answerOverrides:
      analysisTest.type === 'multi-select-number'
        ? (cloneDeep(analysisTest.answerOverrides) as TAnswerForm[])
        : [],
    hasSumValue: analysisTest.type === 'multi-select-number' ? (analysisTest.options.hasSumValue ?? false) : false,
    colorConfigEnabled: config?.enabled ?? false,
    provedColorID: proveConfig?.provedColorID ?? null,
    notProvedColorID: proveConfig?.notProvedColorID ?? null,
    answerColorIDs: { ...(singleSelectConfig?.answerColorIDs ?? {}) },
    // Read top-level, not off colorConfig — the labels print even when
    // classification is disabled or the whole config is null.
    provedTextID: analysisTest.type === 'prove-based' ? (analysisTest.provedTextID ?? '') : '',
    notProvedTextID:
      analysisTest.type === 'prove-based' ? (analysisTest.notProvedTextID ?? '') : '',
    conditions: (valueConfig?.conditions ?? []).map((c) => ({
      _id: generateFirestoreId(),
      operator: c.operator,
      value: c.value,
      colorID: c.colorID,
      textID: c.textID ?? '',
    })),
  }
}

function stripIsNew(answers: TAnswerForm[]): IAnalysisTestAnswer[] {
  return answers.map(({ _isNew: _unused, ...a }) => a)
}

// A single-select test's classification as sent to the API. Only colours for answers that are in
// the form are kept — an answer added and removed again before saving leaves no key behind — and
// inactive answers keep theirs, so an old order still prints in the colour it had.
export function buildSingleSelectColorConfig(
  form: Pick<TAnalysisTestFocusForm, 'colorConfigEnabled' | 'answerColorIDs' | 'answers'>
): IAnalysisTestSingleSelectColorConfig {
  const answerIDs = new Set(form.answers.map((x) => x.id))
  return {
    enabled: form.colorConfigEnabled,
    answerColorIDs: Object.fromEntries(
      Object.entries(form.answerColorIDs).filter(([answerID]) => answerIDs.has(answerID))
    ),
  }
}

// A single-select test's QA gate as sent to the API (MIL-551). The selection only travels beside
// 'answers', and only answers that are still in the form are kept — one added and removed again
// before saving leaves no id behind. Inactive answers keep their place, so a result that already
// holds one still triggers QA.
export function buildSingleSelectQa(
  form: Pick<TAnalysisTestFocusForm, 'qaMode' | 'qaAnswerIDs' | 'answers'>
): { qaTrigger: TSingleSelectQaTrigger | null; qaAnswerIDs: string[] } {
  if (form.qaMode !== 'answers') {
    return { qaTrigger: form.qaMode || null, qaAnswerIDs: [] }
  }
  const answerIDs = new Set(form.answers.map((x) => x.id))
  return {
    qaTrigger: 'answers',
    qaAnswerIDs: form.qaAnswerIDs.filter((answerID) => answerIDs.has(answerID)),
  }
}

// "Ved bestemte svar" with nothing (left) to trigger on — the only invalid state of the QA setting.
export function isSingleSelectQaIncomplete(
  form: Pick<TAnalysisTestFocusForm, 'type' | 'qaMode' | 'qaAnswerIDs' | 'answers'>
): boolean {
  if (form.type !== 'single-select') return false
  const qa = buildSingleSelectQa(form)
  return qa.qaTrigger === 'answers' && qa.qaAnswerIDs.length === 0
}

// Builds the payload entry sent to updateAnalysis's `analysisTests` array.
export function buildAnalysisTestUpdateEntry(form: TAnalysisTestFocusForm): TAnalysisTestUpdateEntry {
  const shared = {
    id: form.id,
    name: form.name,
    displayName: form.displayName,
    isOptional: form.isOptional,
  }

  if (form.type === 'prove-based') {
    return {
      ...shared,
      type: 'prove-based',
      qaTrigger: form.qaTrigger || null,
      provedTextID: form.provedTextID || null,
      notProvedTextID: form.notProvedTextID || null,
      colorConfig: form.colorConfigEnabled
        ? { enabled: true, provedColorID: form.provedColorID, notProvedColorID: form.notProvedColorID }
        : { enabled: false, provedColorID: form.provedColorID, notProvedColorID: form.notProvedColorID },
    }
  }

  if (form.type === 'value-based') {
    return {
      ...shared,
      type: 'value-based',
      hasTooLowToMeasure: form.hasTooLowToMeasure,
      hasTooHighToMeasure: form.hasTooHighToMeasure,
      minLimit: form.minLimit ?? 0,
      maxLimit: form.maxLimit ?? 0,
      unitID: form.unitID,
      colorConfig: {
        enabled: form.colorConfigEnabled,
        conditions: form.conditions.map((c) => ({
          operator: c.operator,
          value: Math.round(Math.max(0, c.value ?? 0) * 10) / 10,
          colorID: c.colorID,
          textID: c.textID || null,
        })),
      },
    }
  }

  if (form.type === 'single-select') {
    return {
      ...shared,
      type: 'single-select',
      answers: stripIsNew(form.answers),
      colorConfig: buildSingleSelectColorConfig(form),
      ...buildSingleSelectQa(form),
    }
  }

  if (form.type === 'multi-select-text') {
    return {
      ...shared,
      type: 'multi-select-text',
      reportDescriptionIDs: form.reportDescriptionIDs,
      suffixReportDescriptionIDs: form.suffixReportDescriptionIDs,
      answers: form.answerMode !== 'none' ? stripIsNew(form.answers) : [],
      answerMode: form.answerMode,
    }
  }

  return {
    ...shared,
    type: 'multi-select-number',
    reportDescriptionIDs: form.reportDescriptionIDs,
    suffixReportDescriptionIDs: form.suffixReportDescriptionIDs,
    unitID: form.unitID,
    answerOverrides: stripIsNew(form.answerOverrides),
    hasSumValue: form.hasSumValue,
  }
}

// Builds the payload sent to analysisTestStore's createAnalysisTest — same
// field mapping as buildAnalysisTestUpdateEntry, minus the id (not yet
// assigned) and with colorConfig omitted entirely when disabled rather than
// sent as `{ enabled: false, ... }`, matching the create endpoint's optional
// colorConfig field.
export function buildAnalysisTestCreatePayload(
  form: TAnalysisTestFocusForm
): TCreateAnalysisTestPayload {
  const shared = {
    name: form.name,
    displayName: form.displayName || undefined,
    isOptional: form.isOptional,
  }

  if (form.type === 'prove-based') {
    return {
      ...shared,
      type: 'prove-based',
      qaTrigger: form.qaTrigger || null,
      provedTextID: form.provedTextID || null,
      notProvedTextID: form.notProvedTextID || null,
      colorConfig: form.colorConfigEnabled
        ? { enabled: true, provedColorID: form.provedColorID, notProvedColorID: form.notProvedColorID }
        : null,
    }
  }

  if (form.type === 'value-based') {
    return {
      ...shared,
      type: 'value-based',
      hasTooLowToMeasure: form.hasTooLowToMeasure,
      hasTooHighToMeasure: form.hasTooHighToMeasure,
      minLimit: form.minLimit ?? 0,
      maxLimit: form.maxLimit ?? 0,
      unitID: form.unitID,
      colorConfig: form.colorConfigEnabled
        ? {
            enabled: true,
            conditions: form.conditions.map((c) => ({
              operator: c.operator,
              value: Math.round(Math.max(0, c.value ?? 0) * 10) / 10,
              colorID: c.colorID,
              textID: c.textID || null,
            })),
          }
        : null,
    }
  }

  if (form.type === 'single-select') {
    return {
      ...shared,
      type: 'single-select',
      answers: stripIsNew(form.answers),
      colorConfig: form.colorConfigEnabled ? buildSingleSelectColorConfig(form) : null,
      ...buildSingleSelectQa(form),
    }
  }

  if (form.type === 'multi-select-text') {
    return {
      ...shared,
      type: 'multi-select-text',
      reportDescriptionIDs: form.reportDescriptionIDs,
      suffixReportDescriptionIDs: form.suffixReportDescriptionIDs,
      answers: form.answerMode !== 'none' ? stripIsNew(form.answers) : [],
      answerMode: form.answerMode,
    }
  }

  return {
    ...shared,
    type: 'multi-select-number',
    reportDescriptionIDs: form.reportDescriptionIDs,
    suffixReportDescriptionIDs: form.suffixReportDescriptionIDs,
    unitID: form.unitID,
    answerOverrides: stripIsNew(form.answerOverrides),
    hasSumValue: form.hasSumValue,
  }
}

// Ported from AnalysisTestEditColorsView.vue — first-match-wins gap/reachability
// validation for value-based colour conditions. See table-conventions equivalent
// business rule description in the Analysis Tests feature doc.
export function computeValueBasedConditionErrors(conditions: TValueBasedConditionRow[]): string[] {
  if (conditions.length === 0) return [t('W: Tilføj mindst én betingelse')]

  const hasEmptyValues = conditions.some((c) => c.value === null)
  if (hasEmptyValues) return [t('W: Udfyld felter som mangler')]

  const validConditions = conditions
    .filter((c) => c.value !== null && c.value >= 0)
    .map((c) => ({ operator: c.operator, value: c.value as number }))

  if (validConditions.length === 0) return [t('W: Tilføj mindst én betingelse')]

  const seen = new Set<string>()
  for (const c of validConditions) {
    const key = `${c.operator}:${c.value}`
    if (seen.has(key)) return [t('W: To eller flere betingelser er identiske')]
    seen.add(key)
  }

  const matches = (v: number, c: { operator: string; value: number }): boolean => {
    switch (c.operator) {
      case '<':
        return v < c.value
      case '<=':
        return v <= c.value
      case '>=':
        return v >= c.value
      case '>':
        return v > c.value
      default:
        return false
    }
  }

  const boundaryValues = [...new Set(validConditions.map((c) => c.value))]
  const highestBoundary = Math.max(...boundaryValues)

  const testPointSet = new Set<number>([0])
  for (const b of boundaryValues) {
    for (const offset of [-0.1, 0, 0.1]) {
      const point = Math.round((b + offset) * 10) / 10
      if (point >= 0) testPointSet.add(point)
    }
  }
  const aboveSentinel = Math.round((highestBoundary + 0.2) * 10) / 10
  testPointSet.add(aboveSentinel)

  const testPoints = [...testPointSet].sort((a, b) => a - b)

  const gapPoints: number[] = []
  for (const v of testPoints) {
    const matchCount = validConditions.filter((c) => matches(v, c)).length
    if (matchCount === 0) gapPoints.push(v)
  }

  const errors: string[] = []

  const unreachableConditions = validConditions.filter(
    (c, i) =>
      !testPoints.some(
        (v) => matches(v, c) && validConditions.slice(0, i).every((prev) => !matches(v, prev))
      )
  )
  if (unreachableConditions.length > 0) {
    const labels = unreachableConditions.map((c) => `${c.operator} ${c.value}`).join(', ')
    errors.push(t('W: Betingelser som aldrig vil blive brugt: {conditions}', { conditions: labels }))
  }

  if (gapPoints.length > 0) {
    const regularGaps = gapPoints.filter((v) => v !== aboveSentinel)
    const hasTopGap = gapPoints.includes(aboveSentinel)
    const gapDescriptions: string[] = []
    if (regularGaps.length > 0) gapDescriptions.push(...regularGaps.map(String))
    if (hasTopGap) gapDescriptions.push(t('W: over {max}', { max: highestBoundary }))
    errors.push(t('W: Ingen betingelse dækker: {gaps}', { gaps: gapDescriptions.join(', ') }))
  }

  return errors
}

export function sortValueBasedConditions(conditions: TValueBasedConditionRow[]): void {
  const priority = (op: string): number => {
    switch (op) {
      case '>=':
        return 0
      case '>':
        return 1
      case '<=':
        return 2
      case '<':
        return 3
      default:
        return 4
    }
  }
  conditions.sort((a, b) => {
    const diff = Number(b.value) - Number(a.value)
    if (diff !== 0) return diff
    return priority(a.operator) - priority(b.operator)
  })
}
